import styled from 'styled-components';
import { EnhancedScopeDataWithBranchName } from '../../scopeData/enhanceScopeData';
import { Graphics } from '../../scopeData/types';
import { readRawValue } from '../../scopeData/utils/readRawValue';
import { getReferenceTypeByReferenceId } from '../../scopeData/utils/referenceType';
import { APIEntityType, ArdoqId } from '@ardoq/api-types';
import { logError } from '@ardoq/logging';
import { ComponentRepresentationWithLink } from './ComponentRepresentationWithLink';
import { useEffect, useRef, useState } from 'react';
import { FlexWrapper, ReferenceTypeName } from './atoms';
import { PlainTextPopover, WithUncontrolledPopover } from '@ardoq/popovers';
import { EnhancedScopeData } from '@ardoq/data-model';

const ReferenceRepresentationWrapper = styled(FlexWrapper)`
  max-width: 800px;
  width: 100%;
`;

const ComponentRepresentationWrapper = styled(FlexWrapper)`
  flex: 1;
  overflow: hidden;
`;
const REFERENCE_TYPE_NAME_MAX_WIDTH = 300;

const useReferenceTypeWidth = (referenceId: ArdoqId) => {
  const referenceTypeRef = useRef<HTMLSpanElement>(null);
  const [referenceTypeWidth, setReferenceTypeWidth] = useState(0);

  useEffect(() => {
    if (referenceTypeRef.current) {
      const width = referenceTypeRef.current.getBoundingClientRect().width;
      setReferenceTypeWidth(width);
    }
  }, [referenceId]);
  return { referenceTypeRef, referenceTypeWidth };
};

const ReferenceRepresentation = ({
  referenceId,
  enhancedScopeData,
  graphics,
  hideDefaultPopover,
  isCommaSeparated,
}: {
  referenceId: ArdoqId;
  enhancedScopeData: EnhancedScopeDataWithBranchName | EnhancedScopeData;
  graphics: Graphics;
  hideDefaultPopover?: boolean;
  isCommaSeparated?: boolean;
}) => {
  const sourceId = readRawValue(
    APIEntityType.REFERENCE,
    referenceId,
    'source',
    enhancedScopeData
  );

  const targetId = readRawValue(
    APIEntityType.REFERENCE,
    referenceId,
    'target',
    enhancedScopeData
  );

  const referenceType = getReferenceTypeByReferenceId(
    referenceId,
    enhancedScopeData
  );

  const { referenceTypeRef, referenceTypeWidth } =
    useReferenceTypeWidth(referenceId);

  if (!referenceType) {
    const errorMessage = "Couldn't find referenceType for reference";
    logError(new Error(errorMessage), errorMessage, {
      referenceId,
    });
    return null;
  }
  const referenceTypeLabelHasOverflow =
    referenceTypeWidth > REFERENCE_TYPE_NAME_MAX_WIDTH;

  return (
    <ReferenceRepresentationWrapper>
      <ComponentRepresentationWrapper>
        <ComponentRepresentationWithLink
          entityId={sourceId}
          graphics={graphics}
          enhancedScopeData={enhancedScopeData}
          hideDefaultPopover={hideDefaultPopover}
        />
      </ComponentRepresentationWrapper>
      <WithUncontrolledPopover
        content={
          referenceTypeLabelHasOverflow ? (
            <PlainTextPopover>{referenceType.name}</PlainTextPopover>
          ) : null
        }
      >
        <ReferenceTypeName
          ref={referenceTypeRef}
          $withEllipsis={referenceTypeLabelHasOverflow}
        >
          {referenceType.name}
        </ReferenceTypeName>
      </WithUncontrolledPopover>
      <ComponentRepresentationWrapper>
        <ComponentRepresentationWithLink
          entityId={targetId}
          graphics={graphics}
          enhancedScopeData={enhancedScopeData}
          hideDefaultPopover={hideDefaultPopover}
          isCommaSeparated={isCommaSeparated}
        />
      </ComponentRepresentationWrapper>
    </ReferenceRepresentationWrapper>
  );
};

export { ReferenceRepresentation };
